import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Settings, Gamepad2, User, Users, Building2, PlugZap, UtensilsCrossed,
  Package, Boxes, ShoppingCart, ClipboardList, BarChart3, TrendingUp,
  Clock3, History, Zap, ChevronDown
} from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import BranchManagement from './BranchManagement';
import SmartPlugManagement from './SmartPlugManagement';
import ProductManagement from './ProductManagement';
import PaketManagement from './PaketManagement';
import IngredientManagement from './IngredientManagement';
import PurchaseManagement from './PurchaseManagement';
import EnergyDashboard from './EnergyDashboard';
import AnalyticsDashboard from './AnalyticsDashboard';
import ShiftSettings from './ShiftSettings';
import ShiftHistory from './ShiftHistory';
import BusinessProfileSettings from './BusinessProfileSettings';
import StockHistoryViewer from './StockHistoryViewer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminDashboard() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  
  const defaultTab = user.role === 'supervisor' ? 'analytics' : 'users';
  const allowedTabs = user.role === 'admin'
    ? ['users', 'branches', 'plugs', 'products', 'packages', 'ingredients', 'purchases', 'stock-history', 'analytics', 'shifts', 'shift-history', 'energy']
    : ['branches', 'plugs', 'products', 'packages', 'ingredients', 'purchases', 'stock-history', 'analytics', 'shifts', 'shift-history', 'energy'];
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTabState] = useState(allowedTabs.includes(requestedTab) ? requestedTab : defaultTab);
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);  // ADD: For supervisor branch display
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [showManagementDropdown, setShowManagementDropdown] = useState(false);
  const [showFnbDropdown, setShowFnbDropdown] = useState(false);
  const [showReportsDropdown, setShowReportsDropdown] = useState(false);
  const [showBusinessSettings, setShowBusinessSettings] = useState(false);
  const [businessProfile, setBusinessProfile] = useState(null);
  const navigate = useNavigate();

  const setActiveTab = (tab) => {
    if (!allowedTabs.includes(tab)) return;
    setActiveTabState(tab);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tab);
    setSearchParams(nextParams);
  };

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (allowedTabs.includes(tabFromUrl)) {
      setActiveTabState(tabFromUrl);
    } else {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', defaultTab);
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams]);

  useEffect(() => {
    fetchUsers();
    fetchBranches();
    fetchBusinessProfile();
  }, []);
  
  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/branches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setBranches(response.data.branches);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchBusinessProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/business-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBusinessProfile(response.data.profile);
    } catch (error) {
      console.error('Error fetching business profile:', error);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowManagementDropdown(false);
        setShowFnbDropdown(false);
        setShowReportsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUsers = async () => {
    console.log('[FETCH USERS] Starting...');
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      console.log('[FETCH USERS] Response:', response.data);
      
      if (response.data.success) {
        console.log('[FETCH USERS] Setting users:', response.data.users);
        // Log each user with their branch info
        response.data.users.forEach((u, i) => {
          console.log(`[FETCH USERS] User ${i}:`, {
            username: u.username,
            branch: u.branch,
            branch_id: u.branch_id,
            updated_at: u.updated_at
          });
        });
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('[FETCH USERS] Error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Session expired. Silakan login kembali.");
        handleLogout();
      }
    } finally {
      setLoading(false);
      console.log('[FETCH USERS] Complete');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!userToDelete) return;

    try {
      const response = await axios.delete(`${API_URL}/api/users/${userToDelete.user_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success) {
        fetchUsers();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setUserToDelete(null);
    }
  };

  return (
    <div className="min-h-screen billing-admin-shell">
      {/* Header - Mobile Responsive */}
      <header className="billing-admin-header sticky top-0 z-40">
        <div className="max-w-[1480px] mx-auto px-3 sm:px-5 lg:px-8 py-3 sm:py-4">
          {/* Mobile Layout */}
          <div className="lg:hidden">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <img src="/logo-rimuru.png" alt="Rimuru" className="h-10 w-auto" />
                <div>
                  <h1 className="text-sm font-bold text-gray-900">Rimuru Game Suite</h1>
                  <p className="text-xs text-gray-600">Admin • {user.full_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Dashboard Kasir Button - Admin & Supervisor */}
                {(user.role === 'admin' || user.role === 'supervisor') && (
                  <button
                    onClick={() => navigate('/kasir')}
                    className="px-2 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1"
                    title="Dashboard Kasir"
                  >
                    <Gamepad2 className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Kasir</span>
                  </button>
                )}
                <button
                  onClick={() => navigate('/profile')}
                  className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5"
                  title="Profile"
                >
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline">Profile</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Logout
                </button>
              </div>
            </div>
            {businessProfile && (
              <div className="billing-business-profile flex items-center gap-2 px-2 py-1.5 rounded-xl">
                {businessProfile.logo_url && (
                  <img 
                    src={businessProfile.logo_url} 
                    alt="Logo" 
                    className="h-8 w-8 object-contain rounded"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-xs truncate">{businessProfile.business_name}</p>
                  <p className="text-xs truncate">{user.branch || 'Semua Cabang'}</p>
                </div>
                <button
                  onClick={() => setShowBusinessSettings(true)}
                  className="billing-header-icon-button p-1.5 rounded-md"
                >
                  <Settings className="h-4 w-4 text-gray-600" />
                </button>
              </div>
            )}
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:flex justify-between items-center">
            {/* Left: Software Branding */}
            <div className="flex items-center gap-4">
              <img src="/logo-rimuru.png" alt="Rimuru Game Suite" className="h-16 w-auto" />
              <div className="border-l border-slate-700 pl-4">
                <p className="text-[11px] uppercase tracking-[0.22em] text-cyan-400 font-bold">Control Center</p>
                <h1 className="text-xl font-bold text-white">Rimuru Game Suite</h1>
                <p className="text-sm text-slate-400">Admin Dashboard • {user.full_name}</p>
              </div>
            </div>

            {/* Right: Business Info + Actions */}
            <div className="flex items-center gap-4">
              {/* Business Profile Display */}
              {businessProfile && (
                <div className="billing-business-profile flex items-center gap-3 px-4 py-2 rounded-xl">
                  {businessProfile.logo_url && (
                    <img 
                      src={businessProfile.logo_url} 
                      alt="Business Logo" 
                      className="h-10 w-10 object-contain rounded"
                    />
                  )}
                  <div className="text-right">
                    <p className="font-bold text-sm">{businessProfile.business_name}</p>
                    <p className="text-xs">{user.branch || 'Semua Cabang'}</p>
                  </div>
                </div>
              )}

              {/* Settings Button - Admin Only */}
              {user.role === 'admin' && (
                <button
                  type="button"
                  onClick={() => setShowBusinessSettings(true)}
                  className="billing-header-icon-button p-2 rounded-lg transition"
                  title="Business Settings"
                >
                  <Settings className="h-5 w-5" />
                </button>
              )}

              {/* Switch to Kasir Dashboard - Admin & Supervisor */}
              {(user.role === 'admin' || user.role === 'supervisor') && (
                <button
                  onClick={() => navigate('/kasir')}
                  className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition flex items-center gap-2"
                >
                  <Gamepad2 className="h-4 w-4" />
                  Dashboard Kasir
                </button>
              )}
              <button
                onClick={() => navigate('/profile')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
              >
                <User className="h-4 w-4" />
                Profile
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="billing-admin-main max-w-[1480px] mx-auto px-3 sm:px-5 lg:px-8 py-5 sm:py-8">
        <div className="billing-admin-intro mb-5 sm:mb-7">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-bold">Workspace Admin</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mt-1">Operasional & Manajemen</h2>
            <p className="text-sm text-slate-400 mt-1">Kelola outlet, perangkat, stok, tim, dan laporan dari satu tempat.</p>
          </div>
          <div className="billing-admin-status hidden sm:flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
            Sistem terhubung
          </div>
        </div>
        {/* Tabs with Dropdown - Mobile Responsive */}
        <div className="billing-admin-nav sticky top-[88px] z-30 mb-5 sm:mb-7">
          <nav className="flex flex-wrap gap-1.5 sm:gap-2 overflow-visible p-1.5">
            {/* Manajemen Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowManagementDropdown(!showManagementDropdown);
                  setShowFnbDropdown(false);
                  setShowReportsDropdown(false);
                }}
                className={`py-3 px-2 sm:py-4 sm:px-4 border-b-2 font-medium text-xs sm:text-sm flex items-center gap-1 whitespace-nowrap ${
                  ['users', 'branches', 'plugs'].includes(activeTab)
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Users className="w-4 h-4" /> Manajemen
                <ChevronDown className="w-4 h-4" />
              </button>
              {showManagementDropdown && (
                <div className="absolute top-full left-0 mt-1 w-48 sm:w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  {/* Hide Manajemen User for Supervisor */}
                  {user.role === 'admin' && (
                    <button
                      onClick={() => { setActiveTab('users'); setShowManagementDropdown(false); }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 text-sm ${activeTab === 'users' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                    >
                      <User className="w-4 h-4" /> Manajemen User
                    </button>
                  )}
                  <button
                    onClick={() => { setActiveTab('branches'); setShowManagementDropdown(false); }}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 text-sm ${activeTab === 'branches' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                  >
                    <Building2 className="w-4 h-4" /> Manajemen Cabang
                  </button>
                  <button
                    onClick={() => { setActiveTab('plugs'); setShowManagementDropdown(false); }}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 text-sm rounded-b-lg ${activeTab === 'plugs' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                  >
                    <PlugZap className="w-4 h-4" /> Smart Plug
                  </button>
                </div>
              )}
            </div>

            {/* F&B Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowFnbDropdown(!showFnbDropdown);
                  setShowManagementDropdown(false);
                  setShowReportsDropdown(false);
                }}
                className={`py-3 px-2 sm:py-4 sm:px-4 border-b-2 font-medium text-xs sm:text-sm flex items-center gap-1 whitespace-nowrap ${
                  ['products', 'packages', 'ingredients', 'purchases'].includes(activeTab)
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <UtensilsCrossed className="w-4 h-4" /> F&amp;B
                <ChevronDown className="w-4 h-4" />
              </button>
              {showFnbDropdown && (
                <div className="absolute top-full left-0 mt-1 w-48 sm:w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <button
                    onClick={() => { setActiveTab('products'); setShowFnbDropdown(false); }}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 text-sm ${activeTab === 'products' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                  >
                    <Package className="w-4 h-4" /> Produk F&amp;B
                  </button>
                  <button
                    onClick={() => { setActiveTab('packages'); setShowFnbDropdown(false); }}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 text-sm ${activeTab === 'packages' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                  >
                    <Boxes className="w-4 h-4" /> Paket
                  </button>
                  <button
                    onClick={() => { setActiveTab('ingredients'); setShowFnbDropdown(false); }}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 text-sm ${activeTab === 'ingredients' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                  >
                    <UtensilsCrossed className="w-4 h-4" /> Bahan Baku
                  </button>
                  <button
                    onClick={() => { setActiveTab('purchases'); setShowFnbDropdown(false); }}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 text-sm ${activeTab === 'purchases' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                  >
                    <ShoppingCart className="w-4 h-4" /> Pembelian Stok
                  </button>
                  <button
                    onClick={() => { setActiveTab('stock-history'); setShowFnbDropdown(false); }}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 text-sm rounded-b-lg ${activeTab === 'stock-history' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                  >
                    <ClipboardList className="w-4 h-4" /> Audit Trail Stok
                  </button>
                </div>
              )}
            </div>

            {/* Reports Dropdown */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowReportsDropdown(!showReportsDropdown);
                  setShowManagementDropdown(false);
                  setShowFnbDropdown(false);
                }}
                className={`py-3 px-2 sm:py-4 sm:px-4 border-b-2 font-medium text-xs sm:text-sm flex items-center gap-1 whitespace-nowrap ${
                  ['analytics', 'shifts', 'shift-history'].includes(activeTab)
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <BarChart3 className="w-4 h-4" /> Laporan
                <ChevronDown className="w-4 h-4" />
              </button>
              {showReportsDropdown && (
                <div className="absolute top-full left-0 mt-1 w-48 sm:w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <button
                    onClick={() => { setActiveTab('analytics'); setShowReportsDropdown(false); }}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 text-sm ${activeTab === 'analytics' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                  >
                    <TrendingUp className="w-4 h-4" /> Analytics &amp; P/L
                  </button>
                  <button
                    onClick={() => { setActiveTab('shifts'); setShowReportsDropdown(false); }}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 text-sm ${activeTab === 'shifts' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                  >
                    <Clock3 className="w-4 h-4" /> Shift Settings
                  </button>
                  <button
                    onClick={() => { setActiveTab('shift-history'); setShowReportsDropdown(false); }}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 text-sm rounded-b-lg ${activeTab === 'shift-history' ? 'bg-blue-50 text-blue-600 font-semibold' : 'text-gray-700'}`}
                  >
                    <History className="w-4 h-4" /> Riwayat Shift
                  </button>
                </div>
              )}
            </div>

            {/* Energy - Standalone */}
            <button
              onClick={() => {
                setActiveTab('energy');
                setShowManagementDropdown(false);
                setShowFnbDropdown(false);
                setShowReportsDropdown(false);
              }}
              className={`py-3 px-2 sm:py-4 sm:px-4 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'energy'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Zap className="w-4 h-4" /> Energy
            </button>

            {/* Stock Opname - Navigate to separate page */}
            <button
              onClick={() => navigate('/stock-opname')}
              className="py-3 px-2 sm:py-4 sm:px-4 border-b-2 border-transparent font-medium text-xs sm:text-sm whitespace-nowrap text-gray-500 hover:text-gray-700 hover:border-gray-300"
            >
              <Boxes className="w-4 h-4" /> Stock Opname
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'branches' ? (
          <BranchManagement />
        ) : activeTab === 'plugs' ? (
          <SmartPlugManagement userRole={user.role} />
        ) : activeTab === 'products' ? (
          <ProductManagement />
        ) : activeTab === 'packages' ? (
          <PaketManagement />
        ) : activeTab === 'ingredients' ? (
          <IngredientManagement />
        ) : activeTab === 'purchases' ? (
          <PurchaseManagement />
        ) : activeTab === 'stock-history' ? (
          <StockHistoryViewer />
        ) : activeTab === 'analytics' ? (
          <AnalyticsDashboard />
        ) : activeTab === 'shifts' ? (
          <ShiftSettings />
        ) : activeTab === 'shift-history' ? (
          <ShiftHistory />
        ) : activeTab === 'energy' ? (
          <EnergyDashboard />
        ) : activeTab === 'users' && user.role === 'admin' ? (
          /* User Management Section - Admin Only */
          <div className="billing-admin-panel rounded-2xl p-3 sm:p-6">
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Manajemen User</h2>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-3 py-1.5 sm:px-4 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">Tambah User</span>
              <span className="sm:hidden">+</span>
            </button>
          </div>

          {/* Users Table */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Memuat data...</p>
            </div>
          ) : (
            <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Username
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nama Lengkap
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cabang
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aksi
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((u) => (
                    <tr key={u.user_id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{u.username}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{u.full_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          u.role === 'admin' ? 'bg-purple-100 text-purple-800' : 
                          u.role === 'supervisor' ? 'bg-orange-100 text-orange-800' : 
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {u.role === 'admin' ? 'Admin' : u.role === 'supervisor' ? 'Supervisor' : 'Kasir'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {u.role === 'supervisor' && u.branch_ids && u.branch_ids.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {u.branch_ids.slice(0, 2).map((branchId, idx) => {
                              const branchObj = branches.find(b => b.branch_id === branchId);
                              return (
                                <span key={idx} className="px-2 py-0.5 bg-orange-50 text-orange-700 text-xs rounded-full border border-orange-200">
                                  {branchObj?.name || branchId.slice(0, 8)}
                                </span>
                              );
                            })}
                            {u.branch_ids.length > 2 && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                +{u.branch_ids.length - 2}
                              </span>
                            )}
                          </div>
                        ) : (
                          u.branch || '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          u.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {u.is_active ? 'Aktif' : 'Nonaktif'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => {
                            setUserToEdit(u);
                            setShowEditModal(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setSelectedUser(u);
                            setShowResetModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          Reset Password
                        </button>
                        {u.user_id !== user.user_id && (
                          <button
                            onClick={() => handleDeleteClick(u)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Hapus
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card Layout */}
            <div className="md:hidden space-y-3">
              {users.map((u) => (
                <div key={u.user_id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{u.username}</p>
                      <p className="text-xs text-gray-600">{u.full_name}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      u.role === 'admin' ? 'bg-purple-100 text-purple-800' : 
                      u.role === 'supervisor' ? 'bg-orange-100 text-orange-800' : 
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {u.role === 'admin' ? 'Admin' : u.role === 'supervisor' ? 'Supervisor' : 'Kasir'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                    <div>
                      <span className="text-gray-500">Cabang:</span>
                      {u.role === 'supervisor' && u.branch_ids && u.branch_ids.length > 0 ? (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {u.branch_ids.slice(0, 2).map((branchId, idx) => {
                            const branchObj = branches.find(b => b.branch_id === branchId);
                            return (
                              <span key={idx} className="px-1.5 py-0.5 bg-orange-50 text-orange-700 text-xs rounded border border-orange-200">
                                {branchObj?.name?.slice(0, 12) || '...'}
                              </span>
                            );
                          })}
                          {u.branch_ids.length > 2 && (
                            <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                              +{u.branch_ids.length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-900 font-medium">{u.branch || '-'}</p>
                      )}
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <p>
                        <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                          u.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {u.is_active ? 'Aktif' : 'Nonaktif'}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setUserToEdit(u);
                        setShowEditModal(true);
                      }}
                      className="flex-1 text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setSelectedUser(u);
                        setShowResetModal(true);
                      }}
                      className="flex-1 text-xs px-3 py-1.5 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                    >
                      Reset Password
                    </button>
                    {u.role !== 'admin' && (
                      <button
                        onClick={() => handleDeleteClick(u)}
                        className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
                      >
                        Hapus
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
        ) : (
          /* Fallback for unauthorized access or invalid tab */
          <div className="bg-white rounded-lg shadow-sm p-6 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Akses Terbatas</h3>
            <p className="text-gray-500">Anda tidak memiliki izin untuk mengakses halaman ini.</p>
          </div>
        )}
      </main>

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchUsers();
          }}
          token={token}
        />
      )}

      {/* Edit User Modal */}
      {showEditModal && userToEdit && (
        <EditUserModal
          user={userToEdit}
          onClose={() => {
            setShowEditModal(false);
            setUserToEdit(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setUserToEdit(null);
            fetchUsers();
          }}
          token={token}
        />
      )}

      {/* Reset Password Modal */}
      {showResetModal && selectedUser && (
        <ResetPasswordModal
          user={selectedUser}
          onClose={() => {
            setShowResetModal(false);
            setSelectedUser(null);
          }}
          onSuccess={() => {
            setShowResetModal(false);
            setSelectedUser(null);
          }}
          token={token}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteConfirmed}
        title="Konfirmasi Hapus User"
        description={`Apakah Anda yakin ingin menghapus user "${userToDelete?.username}"? Tindakan ini tidak dapat dibatalkan.`}
        confirmText="Ya, Hapus"
        cancelText="Batal"
        variant="danger"
      />

      {/* Business Profile Settings Modal */}
      {showBusinessSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="w-full max-w-4xl">
            <BusinessProfileSettings 
              onClose={() => {
                setShowBusinessSettings(false);
                fetchBusinessProfile(); // Refresh business profile after save
              }} 
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Create User Modal Component
function CreateUserModal({ onClose, onSuccess, token }) {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    role: 'kasir',
    branch: '',
    branch_id: '',
    branch_ids: []  // For supervisor with multiple branches
  });
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/branches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setBranches(response.data.branches);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/users`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        toast.success('User berhasil dibuat!', {
          description: 'User baru telah ditambahkan ke sistem',
          duration: 3000
        });
        onSuccess();
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Gagal membuat user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Tambah User Baru</h3>
        
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({...formData, username: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nama Lengkap</label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({...formData, full_name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value, branch_id: '', branch_ids: []})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="kasir">Kasir</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Single Branch for Kasir */}
          {formData.role === 'kasir' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cabang *</label>
              <select
                value={formData.branch_id}
                onChange={(e) => {
                  const selectedBranch = branches.find(b => b.branch_id === e.target.value);
                  setFormData({
                    ...formData, 
                    branch_id: e.target.value,
                    branch: selectedBranch?.name || ''
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Pilih Cabang</option>
                {branches.map(branch => (
                  <option key={branch.branch_id} value={branch.branch_id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Multiple Branches for Supervisor */}
          {formData.role === 'supervisor' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Cabang (bisa pilih lebih dari 1) *</label>
              <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-3">
                {branches.map(branch => (
                  <label key={branch.branch_id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={formData.branch_ids.includes(branch.branch_id)}
                      onChange={(e) => {
                        const newBranchIds = e.target.checked
                          ? [...formData.branch_ids, branch.branch_id]
                          : formData.branch_ids.filter(id => id !== branch.branch_id);
                        setFormData({...formData, branch_ids: newBranchIds});
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{branch.name}</span>
                  </label>
                ))}
              </div>
              {formData.branch_ids.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {formData.branch_ids.length} cabang dipilih
                </p>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {loading ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Reset Password Modal Component
function ResetPasswordModal({ user, onClose, onSuccess, token }) {
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.put(
        `${API_URL}/api/users/${user.user_id}/password`,
        { new_password: newPassword },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        toast.success('Password berhasil direset!', {
          description: 'User dapat login dengan password baru',
          duration: 3000
        });
        onSuccess();
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Gagal reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Reset Password</h3>
        <p className="text-sm text-gray-600 mb-4">
          Reset password untuk user: <span className="font-semibold">{user.username}</span>
        </p>
        
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password Baru</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
              minLength={6}
              placeholder="Minimal 6 karakter"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {loading ? 'Menyimpan...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit User Modal Component
function EditUserModal({ user, onClose, onSuccess, token }) {
  const [formData, setFormData] = useState({
    full_name: user.full_name || '',
    role: user.role || 'kasir',
    branch: user.branch || '',
    branch_id: user.branch_id || '',
    branch_ids: user.branch_ids || [],  // For supervisor with multiple branches
    is_active: user.is_active ?? true
  });
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/branches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setBranches(response.data.branches);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('[EDIT USER] Submitting...', {
      user_id: user.user_id,
      formData: formData,
      url: `${API_URL}/api/users/${user.user_id}`
    });

    try {
      const response = await axios.put(
        `${API_URL}/api/users/${user.user_id}`,
        formData,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      console.log('[EDIT USER] Response:', response.data);

      if (response.data.success) {
        console.log('[EDIT USER] Success! Calling onSuccess()');
        onSuccess();
      }
    } catch (error) {
      console.error('[EDIT USER] Error:', error);
      setError(error.response?.data?.detail || 'Gagal update user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Edit User</h3>
        <p className="text-sm text-gray-600 mb-4">
          Username: <span className="font-semibold">{user.username}</span>
        </p>
        
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nama Lengkap</label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData({...formData, full_name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value, branch_id: '', branch_ids: []})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="kasir">Kasir</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Single Branch for Kasir */}
          {formData.role === 'kasir' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cabang *</label>
              <select
                value={formData.branch_id}
                onChange={(e) => {
                  const selectedBranch = branches.find(b => b.branch_id === e.target.value);
                  setFormData({
                    ...formData, 
                    branch_id: e.target.value,
                    branch: selectedBranch?.name || ''
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Pilih Cabang</option>
                {branches.map(branch => (
                  <option key={branch.branch_id} value={branch.branch_id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Cabang saat ini: {user.branch || '-'}
              </p>
            </div>
          )}

          {/* Multiple Branches for Supervisor */}
          {formData.role === 'supervisor' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Cabang (bisa pilih lebih dari 1) *</label>
              <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-3">
                {branches.map(branch => (
                  <label key={branch.branch_id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={formData.branch_ids.includes(branch.branch_id)}
                      onChange={(e) => {
                        const newBranchIds = e.target.checked
                          ? [...formData.branch_ids, branch.branch_id]
                          : formData.branch_ids.filter(id => id !== branch.branch_id);
                        setFormData({...formData, branch_ids: newBranchIds});
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{branch.name}</span>
                  </label>
                ))}
              </div>
              {formData.branch_ids.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {formData.branch_ids.length} cabang dipilih
                </p>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              className="w-4 h-4"
            />
            <label htmlFor="is_active" className="text-sm font-medium text-gray-700 cursor-pointer">
              User Aktif
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {loading ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </form>
      </div>

    </div>
  );
}

export default AdminDashboard;
